import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function Diagram({ diagram }) {
  const items = Array.isArray(diagram.items) ? diagram.items : [];
  const horizontal = diagram.type === 'flow';
  return <section className="mx-auto my-8 w-full max-w-4xl rounded-3xl border border-emerald-100 bg-gradient-to-br from-white to-emerald-50/40 p-5 sm:p-7 shadow-lg shadow-emerald-900/5" data-ai-diagram="true">
    <div className="mb-5 flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-xl">✦</span><div><h2 className="text-xl font-bold text-slate-900">{diagram.title}</h2>{diagram.subtitle && <p className="mt-1 text-sm text-slate-600">{diagram.subtitle}</p>}</div></div>
    <div className={horizontal ? 'grid gap-3 md:grid-cols-3' : 'space-y-3'}>
      {items.map((item, index) => <React.Fragment key={`${item.title}-${index}`}>
        <div className="relative min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">{index + 1}</span><h3 className="font-semibold text-slate-900">{item.title}</h3></div>
          {item.description && <p className="text-sm leading-6 text-slate-600">{item.description}</p>}
        </div>
        {!horizontal && index < items.length - 1 && <div className="pl-7 text-xl text-emerald-600">↓</div>}
      </React.Fragment>)}
    </div>
  </section>;
}

export default function GlobalCustomization() {
  const location = useLocation();
  const [diagrams, setDiagrams] = useState([]);
  const [mounts, setMounts] = useState([]);
  useEffect(() => { document.body.dataset.sitePath = location.pathname; }, [location.pathname]);
  useEffect(() => {
    let observer;
    let replacements = [];
    let applying = false;
    const applyTexts = () => {
      if (applying || !replacements.length) return;
      applying = true;
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const parent = node.parentElement;
        if (!parent || ['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'OPTION'].includes(parent.tagName)) return;
        replacements.forEach((item) => { if (item?.from && item?.to && node.nodeValue.includes(item.from)) node.nodeValue = node.nodeValue.split(item.from).join(item.to); });
      });
      applying = false;
    };
    fetch(`${API_URL}/api/customization?cache=${Date.now()}`)
      .then((response) => response.ok ? response.json() : {})
      .then((data) => {
        replacements = Array.isArray(data.text_replacements) ? data.text_replacements : [];
        setDiagrams(Array.isArray(data.diagrams) ? data.diagrams : []);
        let style = document.getElementById('ai-global-customization');
        if (!style) { style = document.createElement('style'); style.id = 'ai-global-customization'; document.head.appendChild(style); }
        style.textContent = typeof data.custom_css === 'string' ? data.custom_css : '';
        applyTexts();
        observer = new MutationObserver(applyTexts);
        observer.observe(document.body, { childList: true, subtree: true });
      }).catch(() => {});
    return () => observer?.disconnect();
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      document.querySelectorAll('.ai-diagram-mount').forEach((node) => node.remove());
      const pageCandidates = [...document.querySelectorAll('h1,h2,h3,h4,p,li,span')];
      const active = diagrams.filter((item) => {
        if (document.querySelector('[data-native-diagram]')) return false;
        if (item.page_path === location.pathname || item.page_path === '*') return true;
        if (!item.placement_text) return false;
        return pageCandidates.some((element) => element.textContent?.includes(item.placement_text));
      });
      const created = active.map((diagram) => {
        const mount = document.createElement('div');
        mount.className = 'ai-diagram-mount';
        const contentRoot = document.querySelector('[data-testid="fiche-content"] .max-w-3xl') || document.querySelector('[data-testid="fiche-content"]') || document.querySelector('main') || document.getElementById('root');
        const contentCandidates = [...contentRoot.querySelectorAll('h1,h2,h3,h4,p,li,span')];
        const target = diagram.placement_text ? contentCandidates.find((element) => element.textContent?.includes(diagram.placement_text)) : null;
        if (target) target.insertAdjacentElement('afterend', mount);
        else contentRoot.appendChild(mount);
        return { diagram, mount };
      });
      setMounts(created);
    }, 150);
    return () => { window.clearTimeout(timer); document.querySelectorAll('.ai-diagram-mount').forEach((node) => node.remove()); };
  }, [diagrams, location.pathname]);
  return <>{mounts.map(({ diagram, mount }, index) => createPortal(<Diagram diagram={diagram}/>, mount, `${diagram.title}-${index}`))}</>;
}
